import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Shield, Zap, Plus } from 'lucide-react';
import { usePopularCards, useCollectionShowcase } from '@/hooks/useApi';
import { formatPrice } from '@/utils';
import SEO from '@/components/SEO';

// ── Placeholder styles ────────────────────────────────────────────────────────

const PLACEHOLDER_STYLES = [
  { label: 'Pokemon',              bg: 'from-yellow-400 to-orange-500',  text: 'text-white' },
  { label: 'Magic: The Gathering', bg: 'from-indigo-500 to-purple-700',  text: 'text-white' },
  { label: 'Yu-Gi-Oh!',           bg: 'from-gray-700 to-gray-950',       text: 'text-yellow-700 dark:text-yellow-300' },
  { label: 'One Piece',           bg: 'from-red-500 to-rose-800',        text: 'text-white' },
  { label: 'Dragon Ball',         bg: 'from-orange-400 to-yellow-600',   text: 'text-white' },
  { label: 'Digimon',             bg: 'from-blue-400 to-cyan-600',       text: 'text-white' },
  { label: 'Lorcana',             bg: 'from-violet-500 to-fuchsia-700',  text: 'text-white' },
  { label: 'Flesh & Blood',       bg: 'from-red-800 to-gray-950',        text: 'text-orange-700 dark:text-orange-300' },
];

const MIN_CAROUSEL_ITEMS = 10;

// ── Ring carousel ─────────────────────────────────────────────────────────────
// Technique from the CSS template: each panel is rotated around Y by its index
// fraction of 360° and pushed out by --depth so they sit on the rim of a cylinder.
// The whole ring element spins continuously via a CSS animation.
// Depth formula (circumscribed polygon): r = (cardW/2) / tan(π/n)

function CollectionCarousel({ cards }: { cards: any[] }) {
  // Build item list, padding with placeholders if needed
  const placeholderCount = Math.max(0, MIN_CAROUSEL_ITEMS - cards.length);

  type Item =
    | { kind: 'card'; data: any;                             key: string }
    | { kind: 'ph';   style: typeof PLACEHOLDER_STYLES[0];  key: string };

  const real: Item[] = cards.map((c, i) => ({ kind: 'card', data: c, key: `c${i}` }));
  const fill: Item[] = Array.from({ length: placeholderCount }, (_, i) => ({
    kind:  'ph' as const,
    style: PLACEHOLDER_STYLES[i % PLACEHOLDER_STYLES.length],
    key:   `p${i}`,
  }));

  // Interleave placeholders evenly
  const items: Item[] = [];
  if (fill.length === 0) {
    items.push(...real);
  } else {
    const step = real.length > 0 ? Math.round((real.length + fill.length) / fill.length) : 1;
    let ri = 0, fi = 0, pos = 0;
    while (ri < real.length || fi < fill.length) {
      if (fi < fill.length && (pos % step === 0 || ri >= real.length)) items.push(fill[fi++]);
      else if (ri < real.length) items.push(real[ri++]);
      pos++;
    }
  }

  const count = items.length;
  if (count === 0) return null;

  // Card dimensions (px) — kept small so the ring sits compactly
  const W = 110; // card width
  const H = 160; // card height

  // Radius of the ring so cards don't overlap
  const depth = Math.round((W / 2) / Math.tan(Math.PI / count));

  // Total visible height of the stage = card height + perspective foreshortening room
  const stageH = H + 60;

  // CSS custom props passed to the ring element
  const ringVars: React.CSSProperties = {
    ['--count' as any]:       count,
    ['--depth' as any]:       `${depth}px`,
    ['--tilt' as any]:        '-12deg',
    ['--perspective' as any]: '900px',
    ['--duration' as any]:    `${Math.max(18, count * 2)}s`,
    width:  `${W}px`,
    height: `${H}px`,
    position: 'relative',
    transformStyle: 'preserve-3d',
  };

  return (
    <div
      style={{ height: `${stageH}px`, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
    >
      {/* The spinning ring */}
      <div className="ring-carousel" style={ringVars}>
        {items.map((item, i) => {
          const panelStyle: React.CSSProperties = {
            position:  'absolute',
            inset:     0,
            borderRadius: '10px',
            transform: `rotateY(calc(${360 / count}deg * ${i})) translateZ(${depth}px)`,
            backfaceVisibility: 'hidden',
            overflow: 'hidden',
            border: '2px solid rgba(255,255,255,0.18)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
            cursor: 'pointer',
          };

          if (item.kind === 'card') {
            const href = item.data.listing_id
              ? `/listings/${item.data.listing_id}`
              : `/cards/${item.data.card_id}`;
            return (
              <Link
                key={item.key}
                to={href}
                style={panelStyle}
                title={item.data.listing_id ? `${item.data.card_name} — For Sale` : item.data.card_name}
              >
                {item.data.image_url ? (
                  <img
                    src={item.data.image_url}
                    alt={item.data.card_name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    draggable={false}
                    loading="lazy"
                  />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', fontSize: '10px', color: '#9ca3af', textAlign: 'center', background: '#e5e7eb' }}>
                    {item.data.card_name}
                  </div>
                )}
                {/* For-sale badge */}
                {item.data.listing_id && (
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(5,150,105,0.88)', color: 'white', fontSize: '9px', fontWeight: 700, textAlign: 'center', padding: '3px 0', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    For Sale
                  </div>
                )}
              </Link>
            );
          }

          // Placeholder
          const gradMap: Record<string, string> = {
            'from-yellow-400 to-orange-500':  'linear-gradient(135deg, #facc15, #f97316)',
            'from-indigo-500 to-purple-700':  'linear-gradient(135deg, #6366f1, #7e22ce)',
            'from-gray-700 to-gray-950':      'linear-gradient(135deg, #374151, #030712)',
            'from-red-500 to-rose-800':       'linear-gradient(135deg, #ef4444, #9f1239)',
            'from-orange-400 to-yellow-600':  'linear-gradient(135deg, #fb923c, #ca8a04)',
            'from-blue-400 to-cyan-600':      'linear-gradient(135deg, #60a5fa, #0891b2)',
            'from-violet-500 to-fuchsia-700': 'linear-gradient(135deg, #8b5cf6, #a21caf)',
            'from-red-800 to-gray-950':       'linear-gradient(135deg, #991b1b, #030712)',
          };

          return (
            <Link
              key={item.key}
              to="/collection/add"
              style={{ ...panelStyle, background: gradMap[item.style.bg] ?? '#6366f1' }}
              title="Add a card to appear here"
            >
              <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', borderRadius: '8px', border: '2px dashed rgba(255,255,255,0.25)' }}>
                <Plus style={{ width: '20px', height: '20px', color: 'rgba(255,255,255,0.7)' }} />
                <span style={{ fontSize: '8px', fontWeight: 700, color: 'rgba(255,255,255,0.55)', textAlign: 'center', padding: '0 6px', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.3 }}>
                  {item.style.label}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function HomePage() {
  const { data: popularCards, isLoading } = usePopularCards(8);
  const { data: showcaseCards = [] }      = useCollectionShowcase(30);

  return (
    <div>
      <SEO
        url="/"
        description="Buy, sell, and trade Pokemon, Magic: The Gathering, Yu-Gi-Oh! and more. Join thousands of collectors on Open Card Vault."
      />
      {/* Hero */}
      <section className="bg-gradient-to-br from-brand-600 via-brand-700 to-accent-600 text-white">
        <div className="container-custom py-20 lg:py-32">
          <div className="max-w-3xl">
            <h1 className="text-2xl sm:text-4xl lg:text-6xl font-display font-bold mb-3">
              Buy, Sell, and Trade
              <span className="block text-accent-300">Open Card Vault</span>
            </h1>
            <p className="text-lg sm:text-xl text-accent-200 italic mb-6">
              Open your vault, let's see your cards
            </p>
            <p className="text-xl lg:text-2xl text-brand-100 mb-8">
              Join the ultimate marketplace for Pokemon, Magic, Yu-Gi-Oh, and more. Track prices, build your collection, and connect with collectors worldwide.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/explore" className="btn-primary bg-white dark:bg-zinc-900 text-brand-600 dark:text-brand-400 hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800">
                Explore Cards <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/marketplace" className="btn-outline border-white text-white hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900/10">
                Browse Marketplace
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white dark:bg-zinc-900">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400 mb-4">
                <TrendingUp className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold mb-2">Track Your Collection</h3>
              <p className="text-gray-600 dark:text-zinc-400">Organize your cards, build decks, and keep your collection in one place.</p>
            </div>
            <div className="text-center">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400 mb-4">
                <Shield className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold mb-2">Secure Transactions</h3>
              <p className="text-gray-600 dark:text-zinc-400">Buyer protection and seller verification for safe trading.</p>
            </div>
            <div className="text-center">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400 mb-4">
                <Zap className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold mb-2">Instant Listings</h3>
              <p className="text-gray-600 dark:text-zinc-400">List your cards in seconds and reach thousands of collectors.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Collection Ring Carousel */}
      <section className="py-16 bg-white dark:bg-zinc-900 overflow-hidden">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-display font-bold text-gray-900 dark:text-zinc-100">From the Community</h2>
            <p className="text-gray-500 dark:text-zinc-500 text-sm mt-1">
              {showcaseCards.length > 0
                ? 'Hover to pause · click any card to view'
                : 'Be the first to add your cards — spots are waiting'}
            </p>
          </div>
          <CollectionCarousel cards={showcaseCards} />
        </div>
      </section>

      {/* Popular Cards */}
      <section className="py-20 bg-white dark:bg-zinc-900">
        <div className="container-custom">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-display font-bold">Trending Cards</h2>
            <Link to="/explore" className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:text-brand-300 font-medium">
              View All <ArrowRight className="inline h-4 w-4" />
            </Link>
          </div>
          {isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => <div key={i} className="skeleton h-64 rounded-xl" />)}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {popularCards?.map((card) => (
                <Link key={card.id} to={`/cards/${card.id}`} className="card-hover group">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden bg-gray-100 dark:bg-zinc-800 mb-3">
                    {card.image_url && (
                      <img
                        src={card.image_url}
                        alt={card.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      />
                    )}
                  </div>
                  <h3 className="font-semibold text-sm mb-1 truncate">{card.name}</h3>
                  <p className="text-xs text-gray-600 dark:text-zinc-400 mb-2">{card.set_name}</p>
                  {card.market_price && (
                    <p className="text-brand-600 dark:text-brand-400 font-bold">{formatPrice(card.market_price)}</p>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-600 text-white py-20">
        <div className="container-custom text-center">
          <h2 className="text-3xl lg:text-4xl font-display font-bold mb-4">Ready to Start Trading?</h2>
          <p className="text-xl text-brand-100 mb-8 max-w-2xl mx-auto">
            Join thousands of collectors buying, selling, and trading cards every day.
          </p>
          <Link to="/register" className="btn-primary bg-white dark:bg-zinc-900 text-brand-600 dark:text-brand-400 hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800 text-lg px-8 py-3">
            Create Free Account
          </Link>
        </div>
      </section>
    </div>
  );
}
